import React, { useCallback, useEffect, useState } from "react";
import { fromAddress, setKey } from "react-geocode";

import { getDigifarmShape } from "services";

import {
  getGoogleKey,
  isFieldEnrolled,
  triggerTrengo,
  useCancelAsyncOnUnmount,
  useGetAllCarbonProgramOutcomes,
  useGetCarbonProgramByFarmId,
  useGetFarmReportingYear,
  useGetFieldPractices,
  useGetVerificationEditingRequests,
  useTranslation,
  useUserContext,
} from "utils";

import { Coordinates, EDITING_REQUEST_TYPE, FarmDetailsResponse, FieldDetails, FieldShape, FieldVerification, PRACTICE_BUNDLE_SLUG } from "models";

import { Button, Col, Drawer, Icon, Link, MapInteractive, Modal, Option, Popover, Row, Select } from "components";

import { FieldAdmin } from "pages/admin/verifications/QuickFieldAccess";
import { useSubmissionStatus } from "pages/app/all-field-view/hooks/useSubmissionStatus";

type AddressOption = {
  id: string;
  address: string;
  lat: number;
  lng: number;
};

type FieldLocationProps = {
  admin?: boolean;
  farm: FarmDetailsResponse | FieldAdmin["farmAddress"] | null;
  field: FieldDetails | Omit<FieldVerification, "totalFieldModuleVerifications" | "commentCount" | "practiceParticipations" | "regionId"> | null;
  fieldSize: number | null;
  parentFieldShape: FieldShape | null;
  handleChangeShape?: () => void;
  loadingShapeUpdates?: boolean;
  parentLocation: Coordinates | null;
  onClose: () => void;
  setFieldSize: (fieldSize: number | null) => void;
  setParentFieldShape: (fieldShape: FieldShape | null) => void;
  setParentLocation: (location: Coordinates | null) => void;
  setShowAdminModalConfirm?: (show: boolean) => void;
  setShowFieldEditDrawer?: (show: boolean) => void;
  showAdminModalConfirm?: boolean;
  showLocationMessage: () => void;
  showMapDrawer: boolean;
};
export const FieldLocation = ({
  admin,
  farm,
  field,
  fieldSize,
  parentFieldShape,
  handleChangeShape,
  loadingShapeUpdates,
  parentLocation,
  onClose,
  setFieldSize,
  setParentFieldShape,
  setParentLocation,
  setShowAdminModalConfirm,
  setShowFieldEditDrawer,
  showAdminModalConfirm,
  showLocationMessage,
  showMapDrawer,
}: FieldLocationProps) => {
  const { t } = useTranslation(["onboarding", "enums"]);
  const { user } = useUserContext();
  const { data: carbonProgram } = useGetCarbonProgramByFarmId(user?.farm.id || undefined);
  const [addressOptions, setAddressOptions] = useState<AddressOption[]>([]);
  const [location, setLocation] = useState<Coordinates | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [originalShape, setOriginalShape] = useState<FieldShape | null>(null);
  const [shape, setShape] = useState<FieldShape | null>(null);
  const [shapeEdited, setShapeEdited] = useState<boolean>(false);
  const [showPopInfo, setShowPopInfo] = useState<boolean>(false);
  const [trengo, setTrengo] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [timer, setTimer] = useState<NodeJS.Timeout | null>(null);

  const { data: fieldPractices } = useGetFieldPractices({ fieldId: field?.id ?? "", inView: !!field?.id });
  const { data: carbonProgramOutcomes } = useGetAllCarbonProgramOutcomes(user?.farm.id ?? undefined, carbonProgram);
  const { data: activeFarmReportingYear } = useGetFarmReportingYear(user?.farm.id || null);
  const { isDataSubmitted } = useSubmissionStatus(activeFarmReportingYear);
  const { data: verificationEditingRequests } = useGetVerificationEditingRequests({
    farmId: user?.farm.id || "",
  });

  const fieldEnrolled = field?.id ? isFieldEnrolled(carbonProgramOutcomes?.[field.id]) : false;
  const isFirstYearEnrollment = fieldEnrolled
    ? fieldPractices?.availablePracticeBundles.some(
        (bundle) => bundle.startYear === activeFarmReportingYear && bundle.slug === PRACTICE_BUNDLE_SLUG.CROPS && bundle.active
      )
    : false;
  const hasFieldDataShapeEditingRequests = verificationEditingRequests?.some(
    (request) => request.fieldId === field?.id && request.requestType === EDITING_REQUEST_TYPE.FIELD_SHAPE && !request.completedAt
  );

  const signal = useCancelAsyncOnUnmount()();

  /* a field size change of more than 1% requires additional checks: 
  - farm has no carbon program assigned -> allow any change
  - field has no practice participation (not enrolled) -> allow any change
  - field has data shape editing requests -> allow any change
  - first year of participation and no data submitted -> allow any change
  - first year of participation with data submitted -> up to 1% size change allowed
  - second to nth year of participation (no matter the submission status) -> up to 1% size change allowed
  */
  const getShouldLimitSizeChange = () => {
    if (admin) {
      return false;
    }
    if (!carbonProgram || !fieldEnrolled || hasFieldDataShapeEditingRequests) {
      return false;
    }
    if (isFirstYearEnrollment && !isDataSubmitted) {
      return false;
    }
    if ((isFirstYearEnrollment && isDataSubmitted) || (fieldEnrolled && !isFirstYearEnrollment)) {
      return true;
    }
    return false;
  };

  const shouldLimitSizeChange = getShouldLimitSizeChange();
  const sizePercentageChange = typeof fieldSize === "number" && typeof field?.size === "number" ? (Math.abs(fieldSize - field.size) / field.size) * 100 : 0;

  const oldSize = field?.size;
  const newSize = fieldSize;

  const handleChange = (v: string) => {
    const item = addressOptions.filter((item) => item.id === v);
    if (item && item[0]) {
      setLocation(null);
      setTimeout(() => {
        setLocation(item[0]);
      }, 100);
    }
  };

  const handleAddShape = () => {
    setLoading(true);

    if (location) {
      setParentLocation(location);

      getDigifarmShape({
        location,
        signal,
      })
        .then((res) => {
          if (res) {
            setShape({
              fieldId: field?.id ?? "",
              center: location,
              geometry: res.digifarmShape,
              type: "digifarm",
            });
            setOriginalShape({
              fieldId: field?.id ?? "",
              center: location,
              geometry: res.digifarmShape,
              type: "digifarm",
            });
          } else {
            setShape({
              fieldId: field?.id ?? "",
              center: location,
              geometry: [],
              type: "square",
            });
            setOriginalShape({
              fieldId: field?.id ?? "",
              center: location,
              geometry: [],
              type: "square",
            });
          }
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const handleResetShape = () => {
    setLoading(true);
    setShape(originalShape);
    setShapeEdited(false);
  };

  const handleRemoveShape = () => {
    setLocation({
      lat: parentLocation ? parentLocation.lat : 52.5224832,
      lng: parentLocation ? parentLocation.lng : 13.3906564,
    });
    setLoading(true);
    setShape(null);
    setOriginalShape(null);
    setShapeEdited(false);

    if (parentFieldShape && !admin) {
      setParentFieldShape(null);
    }
  };

  const handleSaveShape = () => {
    setShowModal(false);
    if (!location || !shape) return;

    if (!shouldLimitSizeChange || oldSize === newSize) {
      if (oldSize !== newSize) {
        showLocationMessage();
      }
      onClose();
      setParentFieldShape({
        ...parentFieldShape,
        fieldId: field?.id ?? "",
        center: location,
        geometry: shape.geometry,
        type: admin ? "googlePolygon" : "shapeFile",
      });
    }

    if (shouldLimitSizeChange) {
      if (sizePercentageChange <= 1) {
        showLocationMessage();
        onClose();
        setParentFieldShape({
          ...parentFieldShape,
          fieldId: field?.id ?? "",
          center: location,
          geometry: shape.geometry,
          type: admin ? "googlePolygon" : "shapeFile",
        });
      }
    }
  };

  const getModalConfirmContent = () => {
    if (!shouldLimitSizeChange || sizePercentageChange <= 1) {
      return (
        <>
          <h5 className="deep-black-text">{t("fieldDetails.changeFieldSize.title")}</h5>
          <p className="deep-black-text">{t("fieldDetails.changeFieldSize.description")}</p>
          <hr className="deep-black-text" />
        </>
      );
    }

    if (shouldLimitSizeChange && sizePercentageChange > 1) {
      return (
        <>
          <div className="headline">
            <h5 className="deep-black-text">{t("fieldDetails.changeNotPossible.title")}</h5>
            <Icon iconName="tabler/x" className="icon deep-black inline pointer" onClick={() => setShowPopInfo(false)} />
          </div>
          <p className="deep-black-text">{t("fieldDetails.changeNotPossible.description")}</p>
          <Button type="primary" onClick={() => triggerTrengo("chat", trengo, setTrengo, user)}>
            {t("fieldDetails.changeNotPossible.confirm")}
          </Button>
        </>
      );
    }
  };

  const handleMapMove = (center: Coordinates) => {
    setLocation({
      lat: center.lat,
      lng: center.lng,
    });
  };

  const getAddressOptions = (v: string) => {
    fromAddress(v).then(
      (res) => {
        const addressOptions: AddressOption[] = [];
        res.results.forEach((item: { place_id: string; formatted_address: string; geometry: { location: { lat: number; lng: number } } }) => {
          addressOptions.push({
            id: item.place_id,
            address: item.formatted_address,
            lat: item.geometry.location.lat,
            lng: item.geometry.location.lng,
          });
        });

        setAddressOptions(addressOptions);
      },
      () => {
        setAddressOptions([]);
      }
    );
  };

  const searchAddress = (s: string) => {
    if (timer) {
      clearTimeout(timer);
    }
    if (s.length >= 3) {
      setTimer(
        setTimeout(() => {
          getAddressOptions(s);
        }, 1000)
      );
    } else {
      setTimer(null);
    }
  };

  const loadInitialLocation = useCallback(() => {
    const googleKey = getGoogleKey();
    if (googleKey) {
      setKey(googleKey);
    }

    if (!farm) {
      return;
    }

    if (parentLocation) {
      const currentLocation = {
        lat: parentLocation.lat,
        lng: parentLocation.lng,
      };

      setLocation(currentLocation);
    } else if (!field || !field.location) {
      const tryGeocode = async (query: string) => {
        try {
          const res = await fromAddress(query);
          if (res?.results?.[0]?.geometry?.location) {
            return {
              lat: res.results[0].geometry.location.lat,
              lng: res.results[0].geometry.location.lng,
            };
          }
        } catch (error) {
          // Geocoding failed - this is expected, fallback chain will handle it
          // eslint-disable-next-line no-console
          console.error("Geocode failed for query:", query, "error:", error);
        }
        return null;
      };

      const setInitialLocationByFallbacks = async () => {
        const full = `${farm.addressStreet} ${farm.addressNumber}, ${farm.addressCity}, ${farm.zip} ${farm?.region ?? ""} ${farm?.country ?? ""}`;
        const byFull = await tryGeocode(full);
        if (byFull) return setLocation(byFull);

        const byRegion = farm?.region ? await tryGeocode(`${farm.region} ${farm?.country ?? ""}`) : null;
        if (byRegion) return setLocation(byRegion);

        const byZip = farm?.zip ? await tryGeocode(`${farm.zip} ${farm?.country ?? ""}`) : null;
        if (byZip) return setLocation(byZip);

        const byCity = farm?.addressCity ? await tryGeocode(`${farm.addressCity} ${farm?.country ?? ""}`) : null;
        if (byCity) return setLocation(byCity);

        const byCountry = farm?.country ? await tryGeocode(farm.country) : null;
        if (byCountry) return setLocation(byCountry);

        // final fallback: Berlin — should never happen ideally
        setLocation({ lat: 52.5224832, lng: 13.3906564 });
      };

      setInitialLocationByFallbacks();
    } else {
      setLocation({
        lat: 52.5224832,
        lng: 13.3906564,
      });
    }
  }, [farm, field, parentLocation]);

  useEffect(() => {
    setLoading(false);
  }, [shape]);

  useEffect(() => {
    loadInitialLocation();
  }, [farm, loadInitialLocation]);

  return (
    <>
      <Drawer
        title={t("fieldDetails.mapDrawerTitle")}
        footer={
          shape || parentFieldShape ? (
            <Row justify="space-around" align="middle">
              <Col sm={12} md={12}>
                <Link
                  onClick={() => {
                    if (admin) {
                      setShowFieldEditDrawer?.(false);
                    }
                    if (parentFieldShape === null) {
                      handleRemoveShape();
                    } else {
                      handleResetShape();
                    }
                  }}
                >
                  {!admin ? t("fieldDetails.resetShape") : "cancel"}
                </Link>
              </Col>
              <Col sm={12} md={12}>
                {!shouldLimitSizeChange || sizePercentageChange <= 1 ? (
                  <Button block type="primary" loading={loading} onClick={() => (parentFieldShape === null ? handleSaveShape() : setShowModal(true))}>
                    {t("fieldDetails.save")}
                  </Button>
                ) : shouldLimitSizeChange && sizePercentageChange > 1 ? (
                  <Popover className="field-size-change-not-possible" content={getModalConfirmContent()} placement="topRight" visible={showPopInfo}>
                    <Button block type="primary" loading={loading} onClick={() => setShowPopInfo(true)}>
                      {t("fieldDetails.save")}
                    </Button>
                  </Popover>
                ) : null}
              </Col>
            </Row>
          ) : (
            <Button dataTestId="field-location-add-shape-button" block type="primary" onClick={() => handleAddShape()} loading={loading}>
              {t("fieldDetails.addAShape")}
            </Button>
          )
        }
        fullWidth
        visible={showMapDrawer}
        wide
        onClose={
          admin
            ? () => {
                handleRemoveShape();
                setShowFieldEditDrawer?.(false);
              }
            : () => onClose()
        }
      >
        <div className="map-container">
          <div className="address-input">
            {!shape && !parentFieldShape && (
              // @ts-expect-error need to convert to TS
              <Select
                label={t("fieldDetails.searchAddress")}
                name="address"
                rules={[{ required: false }]}
                showSearch
                defaultActiveFirstOption={false}
                showArrow={false}
                filterOption={false}
                onSearch={(v: string) => searchAddress(v)}
                onChange={(v: string) => handleChange(v)}
                className="notRequired"
                dropdownStyle={{ zIndex: 2000 }}
              >
                {addressOptions.map((item) => (
                  <Option key={item.id} value={item.id}>
                    {item.address}
                  </Option>
                ))}
              </Select>
            )}
          </div>

          <div className="map-pin">{!shape && !parentFieldShape && <Icon iconName="klim/map-pin" />}</div>

          {!shapeEdited && !parentFieldShape && !shape && <p className="center label">{t("fieldDetails.placeThePin")}</p>}
          {(parentFieldShape || shape) && !shapeEdited && <p className="center label top">{t("fieldDetails.dragThePoints")}</p>}
          {shapeEdited && (
            <p className="center label top pointer" onClick={() => handleResetShape()}>
              <Icon iconName="tabler/x" className="inline icon" /> {t("fieldDetails.resetChanges")}
            </p>
          )}

          {location && showMapDrawer && !loading && (
            // @ts-expect-error need to convert to TS
            <MapInteractive
              defaultZoom={17}
              editPolygon
              fieldSize={fieldSize}
              geometry={parentFieldShape || shape}
              location={(parentFieldShape && parentFieldShape.center) || location}
              markers={
                (parentFieldShape || shape) && [
                  {
                    location: (parentFieldShape && parentFieldShape.center) || location,
                    fieldSize: true,
                  },
                ]
              }
              onChange={!shape ? ({ center }: { center: Coordinates }) => handleMapMove(center) : () => {}}
              setLocation={setLocation}
              setShape={setShape}
              setShapeEdited={setShapeEdited}
              setFieldSize={setFieldSize}
              zoomControl={true}
            />
          )}
        </div>
      </Drawer>
      <Modal
        cancelText={t("fieldDetails.changeFieldSize.cancel")}
        className="field-size-reduction"
        description={t("fieldDetails.changeFieldSize.description")}
        onCancel={() => setShowModal(false)}
        onOk={() => handleSaveShape()}
        okText={t("fieldDetails.changeFieldSize.confirm")}
        showModal={showModal}
        title={t("fieldDetails.changeFieldSize.title")}
      />
      {admin && (
        <Modal
          cancelText="cancel"
          description="Updating a user's field will also update the footprints and verification data for the selected field"
          okText="update shape"
          showModal={showAdminModalConfirm}
          onOk={handleChangeShape}
          onCancel={() => {
            setShowAdminModalConfirm?.(false);
          }}
          title="Update field shape and size"
          loading={loadingShapeUpdates}
        />
      )}
    </>
  );
};
